import styled from '@emotion/styled';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { HTMLAttributes, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { regionLanguageMap } from 'services/config/region';
import { Descendant } from 'slate';
import toLowerCase from 'utils/toLowerCase';
import {
  LangRecommendTemplateMap,
  RecommendTemplate,
  RecommendTemplateProps,
} from './RecommendTemplate';

export const Container = styled.div`
  margin-top: 16px;
  background: var(--ant-primary-1);
  > h4 {
    line-height: 32px;
    margin-bottom: 0;
    text-indent: 8px;
    border-bottom: solid 1px var(--fett-border-default);
  }
  > div {
    padding: 8px;
    > * {
      background: var(--fett-bg-panel);
      & + * {
        margin-top: 8px;
      }
    }
  }
`;

export interface RecommendTemplatesProps extends HTMLAttributes<HTMLDivElement> {
  region: General.UpperCaseRegion;
  recommendTemplates?: Marketing.Policy['recommendTemplates'];
  onTemplateSelect: (template: Descendant[]) => void;
}

export function RecommendTemplates(props: RecommendTemplatesProps) {
  const { region, recommendTemplates = [], onTemplateSelect, ...otherProps } = props;

  const { t } = useTranslate();

  const systemLang = useSystemLanguageValue();

  const regionLang = region === 'PH' ? 'tl' : regionLanguageMap[toLowerCase(region)];

  const templates = useMemo(() => {
    return recommendTemplates.reduce<Pick<RecommendTemplateProps, 'regionData' | 'systemData'>[]>(
      (res, { multiLingualTemplates }) => {
        const templateMap = multiLingualTemplates.reduce<LangRecommendTemplateMap>((res, cur) => {
          const { lang, ...others } = cur;

          if (lang === 'ph') {
            res.tl = others;
          } else {
            res[lang] = others;
          }

          return res;
        }, {});

        const regionData = templateMap[regionLang];

        const systemData =
          templateMap[systemLang] ||
          templateMap['en-US'] ||
          templateMap[regionLang] ||
          templateMap[Object.keys(templateMap)[0]];

        if (regionData && systemData) {
          res.push({
            regionData,
            systemData,
          });
        }

        return res;
      },
      [],
    );
  }, [recommendTemplates, regionLang, systemLang]);

  if (templates.length === 0) {
    return null;
  }

  return (
    <Container {...otherProps}>
      <h4>{t('aide.推荐回复')}</h4>
      <div>
        {templates.map((data) => {
          return <RecommendTemplate {...data} onTemplateSelect={onTemplateSelect} />;
        })}
      </div>
    </Container>
  );
}
